import { useState, useCallback } from 'react'
import { useShow } from '@refinedev/core'
import type { TPublisherRequest } from '@modules/types'
import { PUBLISHER_EXPAND_PRESETS } from '@modules/api'

interface UseFileUrlProps {
  requestId: number
}

interface FileWithUrl {
  id: number
  signedUrl?: string
}

const requestCache = new Map<
  number,
  { files: FileWithUrl[] | null; promise: Promise<FileWithUrl[] | null> | null }
>()

export const useFileUrl = ({ requestId }: UseFileUrlProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    query: { refetch },
  } = useShow<TPublisherRequest>({
    resource: 'publisher-request',
    id: requestId,
    meta: PUBLISHER_EXPAND_PRESETS.FULL,
    queryOptions: {
      enabled: false,
    },
  })

  const refreshRequest = useCallback(async () => {
    const cached = requestCache.get(requestId)
    if (cached?.promise) {
      return cached.promise
    }

    setIsRefreshing(true)
    setError(null)

    const refreshPromise = (async () => {
      try {
        const result = await refetch()
        const files = result.data?.data?.clientRequest?.files || null
        requestCache.set(requestId, { files: null, promise: null })
        return files
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to refresh request'
        setError(errorMessage)
        requestCache.set(requestId, { files: null, promise: null })
        return null
      } finally {
        setIsRefreshing(false)
      }
    })()

    requestCache.set(requestId, { files: null, promise: refreshPromise })
    return refreshPromise
  }, [refetch, requestId])

  return {
    refreshRequest,
    isRefreshing,
    error,
  }
}
